import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/functional/DataTable/DataTable';
import { createColumnHelper } from '@tanstack/react-table';
import supabaseClient from '@/api/supabaseClient';
import { User } from '@/types/auth';
import { ChildUser } from '@/api/useChildUsers';
import ConfirmDeleteModal from '@/components/modals/ConfirmDeleteModal/ConfirmDeleteModal';

interface OrganizationRow {
  id: string;
  founderName: string;
  organizationName: string;
  invitationLimit: number;
  childUsers: ChildUser[];
}

const columnHelper = createColumnHelper<OrganizationRow>();

const SuperAdminOrganizationsPage: FC = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [allChildUsers, setAllChildUsers] = useState<(ChildUser & { organizationName: string })[]>([]);
  
  // Modal state for organization deletion
  const [orgDeleteModal, setOrgDeleteModal] = useState<{
    isOpen: boolean;
    organizationId: string | null;
    organizationName: string;
  }>({
    isOpen: false,
    organizationId: null,
    organizationName: '',
  });
  
  // Modal state for child user deletion
  const [childDeleteModal, setChildDeleteModal] = useState<{
    isOpen: boolean;
    childUserId: string | null;
    childUserName: string;
  }>({
    isOpen: false,
    childUserId: null,
    childUserName: '',
  });
  
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        // Fetch nonprofit organizations
        const { data: users, error } = await supabaseClient
          .from('users')
          .select('id, name, organization_name, invitation_limit, auth_id')
          .eq('user_type', 'nonprofit');

        if (error) throw error;

        // For each nonprofit, fetch their child users
        const organizationsData = await Promise.all(
          (users as User[]).map(async (user) => {
            // Fetch child users for this nonprofit
            const { data: childUsersData } = await supabaseClient
              .from('users')
              .select('id, name, email, auth_id')
              .eq('created_by', user.id);

            // Get status for each child user
            const childUsers: ChildUser[] = await Promise.all(
              (childUsersData || []).map(async (child) => {
                // Check if user has verified their email
                const { data: authData } = await supabaseClient.auth.admin.getUserById(child.auth_id);
                const status = authData?.user?.email_confirmed_at ? 'Active' : 'Pending';

                return {
                  id: child.id,
                  email: child.email || '',
                  name: child.name || 'N/A',
                  status,
                  createdAt: ''
                };
              })
            );

            return {
              id: user.id,
              founderName: user.name || 'N/A',
              organizationName: user.organization_name || user.name || 'N/A',
              invitationLimit: user.invitation_limit ?? 0,
              childUsers
            };
          })
        );

        setOrganizations(organizationsData);

        // Fetch ALL child users directly
        const { data: allChildUsersData } = await supabaseClient
          .from('users')
          .select('id, name, auth_id, created_by')
          .eq('user_type', 'child_user');

        // Get parent organization names and status for all children
        const allChildren: (ChildUser & { organizationName: string })[] = await Promise.all(
          (allChildUsersData || []).map(async (child) => {
            let parentOrgName = 'N/A';
            
            // Fetch parent organization by created_by
            if (child.created_by) {
              const { data: parentUser } = await supabaseClient
                .from('users')
                .select('name, organization_name')
                .eq('id', child.created_by)
                .single();
              
              if (parentUser) {
                parentOrgName = parentUser.organization_name || parentUser.name || 'N/A';
              }
            }
            
            // Check if user has verified their email
            const { data: authData } = await supabaseClient.auth.admin.getUserById(child.auth_id);
            const status = authData?.user?.email_confirmed_at ? 'Active' : 'Pending';

            return {
              id: child.id,
              name: child.name || 'N/A',
              status,
              createdAt: '',
              organizationName: parentOrgName
            };
          })
        );

        setAllChildUsers(allChildren);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const openOrgDeleteModal = (organizationId: string, organizationName: string) => {
    setOrgDeleteModal({
      isOpen: true,
      organizationId,
      organizationName,
    });
  };

  const closeOrgDeleteModal = () => {
    if (!isDeleting) {
      setOrgDeleteModal({
        isOpen: false,
        organizationId: null,
        organizationName: '',
      });
    }
  };

  const handleRemove = async () => {
    if (!orgDeleteModal.organizationId) return;
    
    try {
      setIsDeleting(true);
      const organizationId = orgDeleteModal.organizationId;
      
      // Get the organization to find their email
      const orgToRemove = organizations.find(org => org.id === organizationId);
      
      if (orgToRemove) {
        // Get the auth_id for this organization
        const { data: userData } = await supabaseClient
          .from('users')
          .select('auth_id')
          .eq('id', organizationId)
          .single();
          
        if (userData?.auth_id) {
          // Get the email from auth
          const { data: authData } = await supabaseClient.auth.admin.getUserById(userData.auth_id);
          const email = authData?.user?.email;
          
          if (email) {
            // Delete from assessment_users table by email
            await supabaseClient
              .from('assessment_users')
              .delete()
              .eq('email', email);
          }
        }
      }

      const { error } = await supabaseClient
        .from('users')
        .delete()
        .eq('id', organizationId);

      if (error) throw error;

      setOrganizations(prev => prev.filter(org => org.id !== organizationId));
      closeOrgDeleteModal();
    } catch (error) {
      console.error('Error removing organization:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const openChildDeleteModal = (childUserId: string, childUserName: string) => {
    setChildDeleteModal({
      isOpen: true,
      childUserId,
      childUserName,
    });
  };

  const closeChildDeleteModal = () => {
    if (!isDeleting) {
      setChildDeleteModal({
        isOpen: false,
        childUserId: null,
        childUserName: '',
      });
    }
  };

  const handleRemoveChildUser = async () => {
    if (!childDeleteModal.childUserId) return;
    
    try {
      setIsDeleting(true);
      const childUserId = childDeleteModal.childUserId;
      
      // Get child user data
      const { data: childUserData } = await supabaseClient
        .from('users')
        .select('auth_id')
        .eq('id', childUserId)
        .single();

      if (!childUserData?.auth_id) {
        console.error('Child user not found');
        return;
      }

      // Get email from auth
      const { data: authData } = await supabaseClient.auth.admin.getUserById(childUserData.auth_id);
      const email = authData?.user?.email;

      if (email) {
        // Delete from assessment_users table by email (this will cascade delete user_assessments and user_responses)
        await supabaseClient
          .from('assessment_users')
          .delete()
          .eq('email', email);
      }

      // Delete from users table
      const { error: deleteUserError } = await supabaseClient
        .from('users')
        .delete()
        .eq('id', childUserId);

      if (deleteUserError) throw deleteUserError;

      // Delete from auth
      await supabaseClient.auth.admin.deleteUser(childUserData.auth_id);

      // Refresh the child users list
      setAllChildUsers(prev => prev.filter(child => child.id !== childUserId));
      closeChildDeleteModal();
    } catch (error) {
      console.error('Error removing child user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInvitationLimitChange = async (organizationId: string, newLimit: number) => {
    try {
      const { error } = await supabaseClient
        .from('users')
        .update({ invitation_limit: newLimit })
        .eq('id', organizationId);

      if (error) throw error;

      setOrganizations(prev =>
        prev.map(org =>
          org.id === organizationId
            ? { ...org, invitationLimit: newLimit }
            : org
        )
      );
    } catch (error) {
      console.error('Error updating invitation limit:', error);
    }
  };

  const columns = [
    columnHelper.accessor('organizationName', {
      header: () => t('Organization Name'),
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('founderName', {
      header: () => t('Responsible Person'),
      cell: info => (
        <span className="text-gray-900">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor('childUsers', {
      header: () => t('Child Users'),
      cell: info => {
        const childUsers = info.getValue();
        return (
          <span className="text-gray-700">
            {childUsers.length} child user{childUsers.length !== 1 ? 's' : ''}
          </span>
        );
      },
    }),
    columnHelper.accessor('invitationLimit', {
      header: () => t('Invitation Limit'),
      cell: info => (
        <input
          type="number"
          min="0"
          value={info.getValue()}
          onChange={(e) => {
            const newValue = parseInt(e.target.value);
            if (!isNaN(newValue) && newValue >= 0) {
              handleInvitationLimitChange(info.row.original.id, newValue);
            }
          }}
          onBlur={(e) => {
            const value = e.target.value;
            if (value === '' || isNaN(parseInt(value))) {
              handleInvitationLimitChange(info.row.original.id, 0);
            }
          }}
          className="w-20 px-2 py-1 border border-gray-300 rounded-md"
        />
      ),
    }),
    columnHelper.accessor('id', {
      header: () => t('Action'),
      cell: info => (
        <button
          onClick={() => openOrgDeleteModal(info.getValue(), info.row.original.organizationName)}
          className="px-3 py-1 text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
        >
          {t('Remove')}
        </button>
      ),
    }),
  ];

  // Columns for child users table
  type ChildUserWithOrg = ChildUser & { organizationName: string };
  const childUserColumnHelper = createColumnHelper<ChildUserWithOrg>();
  const childUserColumns = [
    childUserColumnHelper.accessor('organizationName', {
      header: () => t('Parent Organization'),
      cell: info => (
        <span className="text-gray-900 font-medium">{info.getValue()}</span>
      ),
    }),
    childUserColumnHelper.accessor('name', {
      header: () => t('Name'),
      cell: info => (
        <span className="text-gray-900">{info.getValue()}</span>
      ),
    }),

    childUserColumnHelper.accessor('status', {
      header: () => t('Status'),
      cell: info => (
        <span className={`inline-flex text-sm font-medium ${
          info.getValue() === 'Active' ? 'text-green-600' : 'text-yellow-600'
        }`}>
          {info.getValue()}
        </span>
      ),
    }),
    childUserColumnHelper.accessor('id', {
      header: () => t('Action'),
      cell: info => (
        <button
          onClick={() => openChildDeleteModal(info.getValue(), info.row.original.name)}
          className="px-3 py-1 text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
        >
          {t('Remove')}
        </button>
      ),
    }),
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
          {t('Organizations')}
        </div>
        <div className="text-center py-8">{t('Loading organizations...')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className='bg-white rounded-2xl px-6 py-4 shadow-assessment-sm flex justify-between items-center'>
        <h1 className='text-4xl font-bold'>{t('Organizations')}</h1>
        <div className="flex items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Search organizations')}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <DataTable 
        data={organizations.filter(org => 
          org.organizationName.toLowerCase().includes(searchQuery.toLowerCase())
        )} 
        columns={columns} 
        className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe overflow-hidden" 
      />

      {/* Child Users Table - Always visible */}
      <div className="flex flex-col gap-4 mt-6">
        <div className='bg-white rounded-2xl px-6 py-4 shadow-assessment-sm'>
          <h2 className='text-2xl font-bold'>{t('All Child Users')}</h2>
        </div>

        {allChildUsers.length === 0 ? (
          <div className="bg-white rounded-2xl px-6 py-8 shadow-assessment-sm text-center text-gray-500">
            {t('No child users found')}
          </div>
        ) : (
          <DataTable 
            data={allChildUsers} 
            columns={childUserColumns} 
            className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe overflow-hidden" 
          />
        )}
      </div>

      {/* Delete Organization Modal */}
      <ConfirmDeleteModal
        isOpen={orgDeleteModal.isOpen}
        onClose={closeOrgDeleteModal}
        onConfirm={handleRemove}
        title={t('Confirm Delete Organization')}
        message={t('Are you sure you want to delete organization "{{name}}"? This action cannot be undone and will also remove all associated data.', { name: orgDeleteModal.organizationName })}
        isLoading={isDeleting}
      />

      {/* Delete Child User Modal */}
      <ConfirmDeleteModal
        isOpen={childDeleteModal.isOpen}
        onClose={closeChildDeleteModal}
        onConfirm={handleRemoveChildUser}
        title={t('Confirm Delete Child User')}
        message={t('Are you sure you want to delete user "{{name}}"? This action cannot be undone and will remove all their assessment data.', { name: childDeleteModal.childUserName })}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SuperAdminOrganizationsPage;
